/**
 * Christmas quote prints — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const christmasQuotePrintsPack: ToilePack = {
    slug: 'christmas-quote-prints',
    category: 'season',
    name: 'Christmas quote prints',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'carol', label: 'Carol line', subject: 'a vertical print lettered "Let it snow" in a warm serif with a scatter of snowflakes'},
        {key: 'joy', label: 'One word', subject: 'a vertical print with the single word "JOY" lettered large and a sprig of holly beneath'},
        {key: 'motto', label: 'Family motto', subject: 'a vertical print lettered "Home for the holidays" in script inside a wreath'},
    ],
    styles: [
        {key: 'scandi', label: 'Scandi folk', style: 'scandinavian folk illustration, simplified shapes, no outlines, matte flat colour', palette: 'A limited palette of red, white, forest green and a touch of gold.', framing: 'Centered on a solid cream background, built to read clean at small size.'},
        {key: 'vintage', label: 'Vintage card', style: 'mid-century christmas card illustration, soft halftone grain, slight misregistration', palette: 'Muted red, pine green, cream and a faded gold.', framing: 'Centered on an aged cream paper background with an even margin.'},
        {key: 'cozy', label: 'Cozy cartoon', style: 'cozy cartoon illustration, rounded shapes, thick soft outlines, warm and friendly', palette: 'Warm reds and greens with a glowing amber and snow white.', framing: 'Centered on a solid background with a little room around it.'},
    ],
    directions: ['a bible verse instead', 'for a nursery wall', 'in gold foil style'],
    directionHint: 'try: a bible verse instead',
    seo: {
        title: 'Christmas Quote Prints: Carol, Joy, Family Motto',
        metaDescription: 'Nine christmas quote prints -- carol line, one-word joy, family motto -- in scandi folk, vintage card, and cozy cartoon finishes.',
        h1: 'Christmas quote print: carol line, one-word joy, and family motto',
        tagline: 'Nine prints, three lettered lines, three finishes each.',
        intro: "Nine typographic christmas quote prints: a 'Let it snow' carol line, a single lettered 'JOY', and a 'Home for the holidays' family motto inside a wreath. Three finishes -- scandi folk, vintage card, and cozy cartoon -- for framed wall art or a printable gift. Ask for a bible verse instead, or a gold foil style, and the same three finishes carry it.",
        useCases: [
            'a framed wall print',
            'a printable holiday gift',
            'a mantel or gallery wall display',
            'a greeting card insert',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine lettered prints -- a carol line, a single word, and a family motto -- each in scandi folk, vintage card, and cozy cartoon finishes, quiet and typographic.'},
            {q: 'What size frame does this fit?', a: 'The prints render at a tall portrait ratio, matching a standard 8x10in or 11x14in frame -- scale to your exact size at printing.'},
            {q: 'Can I change the wording?', a: 'Not on this pack -- the lettering is fixed per design, but the direction field can swap the whole quote, such as a bible verse instead.'},
            {q: "Is this good for a nursery or kids' room?", a: 'Yes -- the direction field can retarget any design for a nursery wall while keeping the same typographic register.'},
        ],
    },
}
